import {
  Caption1,
  Menu,
  MenuDivider,
  MenuItem,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tab,
  TabList,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowSort16Regular,
  ChevronDown12Regular,
  ChevronLeft16Regular,
  ChevronRight16Regular,
  Delete16Regular,
  Dismiss16Regular,
  Document16Regular,
  DocumentBulletListMultipleRegular,
  DocumentMultiple16Regular,
  Filter16Regular,
  Link16Regular,
  LockClosed12Regular,
  MoreHorizontal16Regular,
  Note16Regular,
  People16Regular,
  Person16Regular,
} from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ConfirmDialog } from "@/components/app-dialog";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { FilterLabel, ListSearch, ListToolbar, menuBackLabel, useListToolbarStyles } from "@/components/list-toolbar";
import { contentShell, listBody, shellGutter } from "@/shell/content-frame";
import { usePageAction } from "@/shell/page-action";
import type { DocumentKind, PlanDocument } from "@/engine/types";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { AssigneeAvatars } from "@/features/tasks/task-ui";
import { formatDate } from "@/lib/date-format";
import { useCan } from "@/features/access/use-plan-access";
import { useProjectStore } from "@/store/project-store";
import { DocTemplateModal, type DocTemplateModalIntent } from "./DocTemplateModal";
import { blocksFromOffer, blocksFromSections } from "./doc-templates";
import { canWorkspace, useEffectiveWorkspaceRole } from "@/features/access/workspace-access";
import { DocKindIcon, DocLinkMark, DocLinkMore } from "./DocLinkChip";
import { DOCUMENT_LINK_PREVIEW, documentIsUnlinked, linkCountSummary, presentLink } from "./doc-links";
import { openDocumentTarget } from "./doc-open";
import {
  DOCUMENT_KINDS,
  DOCUMENT_KIND_LABEL,
  DOCUMENT_VISIBILITY_LABEL,
  MISSING_DOCUMENT_MESSAGE,
  documentCode,
  documentLibraryScale,
  documentMatchesQuery,
  documentTitle,
  parseDocLinkFilter,
  parseDocSection,
  sortDocuments,
  type DocLinkFilter,
  type DocSection,
  type DocSortKey,
} from "./doc-model";
import { linkedExecutionIds, useDocReader } from "./doc-reader";
import {
  canDeleteDocument,
  canMakePrivate,
  canPurgeDocument,
  canReadDocument,
  canReadDocumentContent,
  canRestoreDocument,
  canSeeTrashRow,
} from "./doc-visibility";

type Scope = "all" | "plan" | "private";

const KIND_EMPTY: Record<DocumentKind, { title: string; description: string }> = {
  meeting: { title: "회의록이 없습니다", description: "정한 내용은 작업과 이슈로 연결됩니다" },
  memo: { title: "메모가 없습니다", description: "다듬기 전의 글을 여기에 둡니다" },
  material: { title: "문서가 없습니다", description: "붙여 넣은 글은 여기서 고칩니다" },
};

const SORTS: Array<{ value: DocSortKey; label: string }> = [
  { value: "updated", label: "수정일" },
  { value: "title", label: "제목" },
  { value: "seq", label: "번호" },
];

const useStyles = makeStyles({
  split: {
    display: "inline-flex",
    alignItems: "center",
  },
  main: {
    whiteSpace: "nowrap",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  chevron: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeftWidth: "1px",
    borderLeftStyle: "solid",
    borderLeftColor: tokens.colorNeutralStrokeOnBrand,
  },
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  body: {
    ...listBody,
    padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}`,
    gap: tokens.spacingVerticalS,
  },
  notice: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
  row: {
    cursor: "pointer",
    ":hover td": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active td": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  codeCell: {
    width: "1%",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground2,
  },
  codeInner: {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  titleCell: {
    maxWidth: 0,
    width: "100%",
    minWidth: "240px",
  },
  titleLine: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
    maxWidth: "100%",
  },
  lockIcon: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  shrink: {
    width: "1%",
    whiteSpace: "nowrap",
  },
  links: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "4px",
    maxWidth: "360px",
  },
  muted: { color: tokens.colorNeutralForeground3 },
  nowrap: { whiteSpace: "nowrap", flexShrink: 0 },
  popover: {
    minWidth: "220px",
    maxWidth: "320px",
    maxHeight: "70vh",
    overflowY: "auto",
  },
});

type DocFilterSection = "sort" | "author" | "link";

const FILTER_SECTIONS: DocFilterSection[] = ["sort", "author", "link"];

const SECTION_LABEL: Record<DocFilterSection, string> = {
  sort: "정렬",
  author: "작성",
  link: "연결",
};

function sectionIcon(id: DocFilterSection) {
  if (id === "sort") return <ArrowSort16Regular />;
  if (id === "author") return <Person16Regular />;
  return <Link16Regular />;
}

function sectionChoices(id: DocFilterSection): Array<{ value: string; label: string }> {
  if (id === "sort") return SORTS;
  if (id === "author") {
    return [
      { value: "all", label: "전체" },
      { value: "mine", label: "내 글" },
    ];
  }
  return [
    { value: "all", label: "전체" },
    { value: "none", label: "연결 없음" },
  ];
}

/** 작성과 연결을 한 메뉴에 둔다. 정렬은 그 하위이고, 도구 줄의 정렬 버튼은 없다. */
export function DocFilterMenu({
  mine,
  link,
  sort,
  onAuthor,
  onLink,
  onSort,
  onReset,
}: {
  mine: boolean;
  link: DocLinkFilter;
  sort: DocSortKey;
  onAuthor: (value: "all" | "mine") => void;
  onLink: (value: DocLinkFilter) => void;
  onSort: (value: DocSortKey) => void;
  onReset: () => void;
}) {
  const styles = useStyles();
  const toolbar = useListToolbarStyles();
  const [section, setSection] = useState<DocFilterSection | null>(null);
  const backRef = useRef<HTMLDivElement>(null);
  const rootRefs = useRef(new Map<DocFilterSection, HTMLDivElement>());
  const returnFocus = useRef<DocFilterSection | null>(null);
  const applied = (mine ? 1 : 0) + (link === "none" ? 1 : 0);
  const sole = applied === 1 ? (mine ? "내 글" : "연결 없음") : undefined;

  const openSection = (next: DocFilterSection) => setSection(next);
  const goBack = () => {
    returnFocus.current = section;
    setSection(null);
  };
  useEffect(() => {
    if (section) {
      backRef.current?.focus();
      return;
    }
    const from = returnFocus.current;
    if (!from) return;
    returnFocus.current = null;
    rootRefs.current.get(from)?.focus();
  }, [section]);

  const hintFor = (id: DocFilterSection): string | undefined => {
    if (id === "sort") return SORTS.find((item) => item.value === sort)?.label;
    if (id === "author") return mine ? "내 글" : undefined;
    return link === "none" ? "연결 없음" : undefined;
  };

  return (
    <Menu
      checkedValues={{
        sort: [sort],
        author: [mine ? "mine" : "all"],
        link: [link],
      }}
      onCheckedValueChange={(_, data) => {
        const next = data.checkedItems[0];
        if (data.name === "author" && (next === "all" || next === "mine")) onAuthor(next);
        if (data.name === "link" && (next === "all" || next === "none")) onLink(next);
        if (data.name === "sort" && (next === "updated" || next === "title" || next === "seq")) onSort(next);
      }}
      onOpenChange={(_, data) => {
        if (!data.open) setSection(null);
      }}
    >
      <MenuTrigger disableButtonEnhancement>
        <FuiButton
          className={toolbar.nowrap}
          icon={<Filter16Regular />}
          appearance="subtle"
          aria-label={applied === 0 ? "필터" : sole ? `필터: ${sole}` : `필터: ${applied}`}
        >
          <FilterLabel info={sole} count={applied > 1 ? applied : undefined} />
        </FuiButton>
      </MenuTrigger>
      <MenuPopover
        className={styles.popover}
        onKeyDown={(event) => {
          if (event.key !== "ArrowLeft" || !section) return;
          event.preventDefault();
          event.stopPropagation();
          goBack();
        }}
      >
        <MenuList>
          {section == null ? (
            <>
              {FILTER_SECTIONS.map((id) => {
                const hint = hintFor(id);
                return (
                  <MenuItem
                    key={id}
                    persistOnClick
                    ref={(node) => {
                      if (node) rootRefs.current.set(id, node);
                      else rootRefs.current.delete(id);
                    }}
                    aria-label={hint ? `${SECTION_LABEL[id]}, ${hint}` : SECTION_LABEL[id]}
                    secondaryContent={hint ? <span className={toolbar.menuHint} title={hint}>{hint}</span> : undefined}
                    submenuIndicator={<ChevronRight16Regular />}
                    icon={sectionIcon(id)}
                    onClick={() => openSection(id)}
                    onKeyDown={(event) => {
                      if (event.key !== "ArrowRight") return;
                      event.preventDefault();
                      event.stopPropagation();
                      openSection(id);
                    }}
                  >
                    {SECTION_LABEL[id]}
                  </MenuItem>
                );
              })}
              {applied > 0 ? (
                <>
                  <MenuDivider />
                  <MenuItem icon={<Dismiss16Regular />} onClick={onReset}>
                    필터 초기화
                  </MenuItem>
                </>
              ) : null}
            </>
          ) : (
            <>
              <MenuItem
                ref={backRef}
                persistOnClick
                icon={<ChevronLeft16Regular />}
                aria-label={menuBackLabel(SECTION_LABEL[section])}
                onClick={goBack}
              >
                {SECTION_LABEL[section]}
              </MenuItem>
              <MenuDivider />
              {sectionChoices(section).map((item) => (
                <MenuItemRadio key={item.value} name={section} value={item.value}>
                  {item.label}
                </MenuItemRadio>
              ))}
            </>
          )}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

function matches(doc: PlanDocument, query: string, readableContent: boolean): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (!readableContent) return `${documentCode(doc.seq)} ${doc.title}`.toLowerCase().includes(q);
  return documentMatchesQuery(doc, q);
}

function docPreviewLine(doc: PlanDocument, readableContent: boolean): string {
  if (!readableContent) return "";
  const first = doc.plainText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find((line) => line.length > 0) ?? "";
  if (doc.kind === "meeting" && doc.heldAt) {
    const held = `회의 ${formatDate(doc.heldAt, "numeric")}`;
    return first ? `${held} · ${first}` : held;
  }
  return first;
}

export function DocList() {
  const styles = useStyles();
  const table = useDataTableStyles();
  const navigate = useNavigate();
  const { id: projectId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const project = useProjectStore((s) => s.project);
  const reader = useDocReader();
  const canCreate = useCan("plan.doc.edit");
  const [cursor, setCursor] = useState(0);
  const [searchRequest, setSearchRequest] = useState(0);
  const [templateApply, setTemplateApply] = useState(true);
  const [templateIntent, setTemplateIntent] = useState<DocTemplateModalIntent | null>(null);
  const workspaceRole = useEffectiveWorkspaceRole();
  const canManageTemplates = canWorkspace(workspaceRole, "workspace.settings.update");
  const [pendingTrash, setPendingTrash] = useState<string | null>(null);
  const [pendingPurge, setPendingPurge] = useState<string | null>(null);
  const [pendingPrivate, setPendingPrivate] = useState<string | null>(null);

  const rawScope = params.get("scope");
  const scope: Scope = rawScope === "plan" || rawScope === "private" ? rawScope : "all";
  const mine = params.get("mine") === "1" || rawScope === "mine";
  const rawKind = params.get("kind");
  const section = parseDocSection(params.get("view"), rawKind);
  const query = params.get("q") ?? "";
  const trash = params.get("tab") === "trash";
  const rawSort = params.get("sort");
  const sort: DocSortKey = rawSort === "title" || rawSort === "seq" ? rawSort : "updated";
  const missing = params.get("missing") === "1";
  const linkFilter = parseDocLinkFilter(params.get("link"));

  useEffect(() => {
    if (!rawKind) return;
    const next = new URLSearchParams(params);
    if (!next.get("view") && section !== "all") next.set("view", section);
    next.delete("kind");
    setParams(next, { replace: true });
  }, [rawKind, params, section, setParams]);

  const patch = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === null || value === "") p.delete(key);
      else p.set(key, value);
    }
    setParams(p, { replace: true });
  };

  const baseDocs = useMemo(
    () =>
      project.documents.filter((doc) =>
        trash ? canSeeTrashRow(doc, reader) : canReadDocument(doc, reader),
      ),
    [project.documents, trash, reader],
  );

  const trashCount = useMemo(
    () => project.documents.filter((doc) => canSeeTrashRow(doc, reader)).length,
    [project.documents, reader],
  );

  const description = useMemo(
    () => documentLibraryScale(project.documents.filter((doc) => canReadDocument(doc, reader))),
    [project.documents, reader],
  );

  const visible = useMemo(() => {
    const rows = baseDocs.filter((doc) => {
      if (scope !== "all" && doc.visibility !== scope) return false;
      if (mine && doc.authorId !== reader.actorId) return false;
      if (linkFilter === "none" && !documentIsUnlinked(project.documentLinks, doc.id)) return false;
      if (section !== "all" && doc.kind !== section) return false;
      return matches(doc, query, canReadDocumentContent(doc, reader));
    });
    return sortDocuments(rows, sort);
  }, [baseDocs, reader, scope, mine, linkFilter, project.documentLinks, section, query, sort]);

  useEffect(() => {
    setCursor((index) => Math.min(index, Math.max(visible.length - 1, 0)));
  }, [visible.length]);

  const create = (nextKind: DocumentKind) => {
    const id = useProjectStore.getState().createDocument(nextKind);
    if (id) navigate(`/projects/${projectId}/docs/${id}`);
  };

  const selectSection = (next: DocSection) => {
    if (next === section) return;
    patch({ view: next === "all" ? null : next, kind: null, q: null });
  };

  usePageAction(null);

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) return;
      event.preventDefault();
      setSearchRequest((count) => count + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const open = (id: string) => navigate(`/projects/${projectId}/docs/${id}`);
  const onRowKey = (event: KeyboardEvent<HTMLTableRowElement>, index: number, id: string) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const next = Math.min(index + 1, visible.length - 1);
      setCursor(next);
      (event.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const next = Math.max(index - 1, 0);
      setCursor(next);
      (event.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      open(id);
    }
  };

  const trashDoc = project.documents.find((doc) => doc.id === pendingTrash);
  const purgeDoc = project.documents.find((doc) => doc.id === pendingPurge);
  const privateDoc = project.documents.find((doc) => doc.id === pendingPrivate);
  const emptyLibrary = baseDocs.length === 0;
  const narrowed = query.trim().length > 0 || scope !== "all" || mine || linkFilter !== "all";
  const showVisibility = scope === "all";
  const kindEmpty = section === "all" ? null : KIND_EMPTY[section];

  return (
    <div className={styles.page}>
      <ListToolbar
        title="문서"
        description={description}
        headingAction={
          canCreate && !trash ? (
            <div className={styles.split}>
              <FuiButton appearance="primary" className={styles.main} icon={<Document16Regular />} onClick={() => create("material")}>
                문서 추가
              </FuiButton>
              <Menu positioning="below-end">
                <MenuTrigger disableButtonEnhancement>
                  <FuiButton appearance="primary" className={styles.chevron} icon={<ChevronDown12Regular />} aria-label="종류" />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    {DOCUMENT_KINDS.map((kind) => (
                      <MenuItem key={kind} icon={<DocKindIcon kind={kind} />} onClick={() => create(kind)}>
                        {DOCUMENT_KIND_LABEL[kind]}
                      </MenuItem>
                    ))}
                    <MenuDivider />
                    <MenuItem icon={<DocumentBulletListMultipleRegular />} onClick={() => {
                      setTemplateApply(true);
                      setTemplateIntent({ type: "browse" });
                    }}>
                      템플릿에서 시작...
                    </MenuItem>
                    {canManageTemplates ? (
                      <MenuItem icon={<DocumentBulletListMultipleRegular />} onClick={() => {
                          setTemplateApply(false);
                          setTemplateIntent({ type: "browse" });
                        }}>
                        템플릿 관리
                      </MenuItem>
                    ) : null}
                  </MenuList>
                </MenuPopover>
              </Menu>
            </div>
          ) : undefined
        }
        sections={
          <TabList
            size="small"
            selectedValue={section}
            aria-label="문서"
            onTabSelect={(_, data) => {
              const next = data.value;
              if (next === "all" || next === "meeting" || next === "memo" || next === "material") selectSection(next);
            }}
          >
            <Tab value="all" icon={<DocumentMultiple16Regular />}>전체</Tab>
            <Tab value="material" icon={<Document16Regular />}>문서</Tab>
            <Tab value="meeting" icon={<People16Regular />}>회의록</Tab>
            <Tab value="memo" icon={<Note16Regular />}>메모</Tab>
          </TabList>
        }
        search={<ListSearch value={query} requestOpen={searchRequest} onChange={(value) => patch({ q: value || null })} />}
        filters={
          <>
            <SegmentedControl
              value={scope}
              ariaLabel="공개 범위"
              onChange={(value) =>
                patch({
                  scope: value === "all" ? null : value,
                  ...(rawScope === "mine" ? { mine: "1" } : {}),
                })
              }
              options={[
                { value: "all", label: "전체" },
                { value: "plan", label: "공개" },
                { value: "private", label: "비공개" },
              ]}
            />
            <DocFilterMenu
              mine={mine}
              link={linkFilter}
              sort={sort}
              onAuthor={(value) => patch({ mine: value === "mine" ? "1" : null, ...(rawScope === "mine" ? { scope: null } : {}) })}
              onLink={(value) => patch({ link: value === "none" ? "none" : null })}
              onSort={(value) => patch({ sort: value === "updated" ? null : value })}
              onReset={() => patch({ mine: null, link: null, ...(rawScope === "mine" ? { scope: null } : {}) })}
            />
          </>
        }
        trail={
          <FuiButton
            className={styles.nowrap}
            appearance="subtle"
            icon={<Delete16Regular />}
            aria-pressed={trash}
            onClick={() => patch({ tab: trash ? null : "trash", missing: null })}
          >
            {trash ? "문서 목록" : trashCount > 0 ? `휴지통 ${trashCount}` : "휴지통"}
          </FuiButton>
        }
      />
      <div className={styles.body}>
        {missing ? (
          <div className={styles.notice}>
            <Caption1>{MISSING_DOCUMENT_MESSAGE}</Caption1>
          </div>
        ) : null}
        {trash ? (
          <div className={styles.notice}>
            <Caption1>지운 문서는 30일 동안 보관된 뒤 영구 삭제됩니다.</Caption1>
          </div>
        ) : null}
        {visible.length === 0 ? (
          trash && baseDocs.length === 0 ? (
            <GhostEmptyState variant="search" title="휴지통이 비어 있습니다" description="" />
          ) : !trash && !narrowed && section === "all" && emptyLibrary ? (
            <GhostEmptyState title="회의와 메모를 문서로 남기세요" description="붙여 넣은 자료는 여기서 고치고, 할 일은 작업과 이슈로 연결합니다" />
          ) : !trash && !narrowed && kindEmpty ? (
            <GhostEmptyState title={kindEmpty.title} description={kindEmpty.description} />
          ) : (
            <GhostEmptyState variant="search" title="일치하는 문서가 없습니다" description="" />
          )
        ) : (
          <div className={table.frame}>
            <table className={table.table} aria-label={trash ? "휴지통" : "문서"}>
              <thead>
                <tr>
                  <th className={table.th}>번호</th>
                  <th className={table.th}>제목</th>
                  <th className={table.th}>종류</th>
                  {showVisibility ? <th className={table.th}>구분</th> : null}
                  <th className={table.th}>작성자</th>
                  <th className={table.th}>연결</th>
                  <th className={mergeClasses(table.th, table.num)}>수정일</th>
                  <th className={table.th}>동작</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((doc, index) => {
                  const summary = linkCountSummary(project.documentLinks.filter((link) => link.documentId === doc.id));
                  const title = documentTitle(doc.title);
                  const linked = linkedExecutionIds(doc.id, project.documentLinks);
                  const canDelete = canDeleteDocument(doc, reader, linked);
                  const canPrivate = canMakePrivate(doc, reader);
                  const readableContent = canReadDocumentContent(doc, reader);
                  const preview = docPreviewLine(doc, readableContent);
                  return (
                    <tr
                      key={doc.id}
                      className={styles.row}
                      tabIndex={index === cursor ? 0 : -1}
                      onClick={() => open(doc.id)}
                      onKeyDown={(event) => onRowKey(event, index, doc.id)}
                      onFocus={() => setCursor(index)}
                    >
                      <td className={mergeClasses(table.td, styles.codeCell)}>
                        <span className={styles.codeInner}>
                          <DocKindIcon kind={doc.kind} />
                          <span>{documentCode(doc.seq)}</span>
                        </span>
                      </td>
                      <td className={mergeClasses(table.td, styles.titleCell)}>
                        <Tooltip content={preview ? `${title}\n${preview}` : title} relationship="description">
                          <span className={table.twoLine}>
                            <span className={styles.titleLine}>
                              <span className={table.clip}>{title}</span>
                              {showVisibility && doc.visibility === "private" ? <LockClosed12Regular className={styles.lockIcon} /> : null}
                            </span>
                            {preview ? <Caption1 className={table.role}>{preview}</Caption1> : null}
                          </span>
                        </Tooltip>
                      </td>
                      <td className={mergeClasses(table.td, styles.shrink)}>{DOCUMENT_KIND_LABEL[doc.kind]}</td>
                      {showVisibility ? (
                        <td className={mergeClasses(table.td, styles.shrink, doc.visibility === "private" && styles.muted)}>
                          {DOCUMENT_VISIBILITY_LABEL[doc.visibility]}
                        </td>
                      ) : null}
                      <td className={mergeClasses(table.td, styles.shrink)} title={doc.authorId || "작성자 미정"}>
                        {doc.authorId ? <AssigneeAvatars names={[doc.authorId]} /> : <span className={styles.muted}>—</span>}
                      </td>
                      <td className={mergeClasses(table.td, summary.count === 0 && styles.muted)}>
                        <LinkMarks docId={doc.id} />
                      </td>
                      <td className={mergeClasses(table.td, table.num, styles.muted)} title={formatDate(doc.updatedAt, "dateTime")}>
                        {formatDate(doc.updatedAt, "numeric")}
                      </td>
                      <td
                        className={mergeClasses(table.td, styles.shrink)}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <RowMenu
                          canTrash={!doc.deletedAt && canDelete}
                          canPrivate={!doc.deletedAt && doc.visibility === "plan" && canPrivate}
                          canShare={!doc.deletedAt && doc.visibility === "private" && canDelete}
                          canRestore={!!doc.deletedAt && canRestoreDocument(doc, reader)}
                          canPurge={!!doc.deletedAt && canPurgeDocument(doc, reader)}
                          onTrash={() => setPendingTrash(doc.id)}
                          onPrivate={() => setPendingPrivate(doc.id)}
                          onShare={() => useProjectStore.getState().setDocumentVisibility(doc.id, "plan")}
                          onRestore={() => useProjectStore.getState().restoreDocument(doc.id)}
                          onPurge={() => setPendingPurge(doc.id)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {templateIntent ? (
        <DocTemplateModal
          open
          intent={templateIntent}
          onClose={() => setTemplateIntent(null)}
          {...(canCreate && !trash
            ? {
                onApply: (offer) => {
                  const store = useProjectStore.getState();
                  const id = store.createDocument(offer.kind);
                  if (!id) return;
                  store.saveDocumentBody(id, blocksFromOffer(offer));
                  setTemplateIntent(null);
                  navigate(`/projects/${projectId}/docs/${id}`);
                },
              }
            : {})}
        />
      ) : null}
      <ConfirmDialog
        open={!!trashDoc}
        title="문서를 휴지통으로 옮길까요"
        message="이 문서를 휴지통으로 옮깁니다. 연결된 작업과 이슈는 남고, 그쪽 목록에서는 이 문서가 잠시 숨습니다."
        confirmLabel="휴지통으로"
        onCancel={() => setPendingTrash(null)}
        onConfirm={() => {
          if (trashDoc) useProjectStore.getState().trashDocument(trashDoc.id);
          setPendingTrash(null);
        }}
      />
      <ConfirmDialog
        open={!!purgeDoc}
        title="문서를 지울까요"
        message="이 문서를 지웁니다. 이 문서의 연결도 함께 사라집니다."
        confirmLabel="지우기"
        onCancel={() => setPendingPurge(null)}
        onConfirm={() => {
          if (purgeDoc) useProjectStore.getState().purgeDocument(purgeDoc.id);
          setPendingPurge(null);
        }}
      />
      <ConfirmDialog
        open={!!privateDoc}
        title="비공개로 바꿀까요?"
        message="다른 멤버의 목록과 연결 칩에서 이 문서가 사라집니다. 작업과 이슈는 그대로입니다."
        confirmLabel="비공개로 전환"
        onCancel={() => setPendingPrivate(null)}
        onConfirm={() => {
          if (privateDoc) useProjectStore.getState().setDocumentVisibility(privateDoc.id, "private");
          setPendingPrivate(null);
        }}
      />
    </div>
  );
}

export function RowMenu({
  canTrash,
  canPrivate,
  canShare,
  canRestore,
  canPurge,
  onTrash,
  onPrivate,
  onShare,
  onRestore,
  onPurge,
}: {
  canTrash: boolean;
  canPrivate: boolean;
  canShare: boolean;
  canRestore: boolean;
  canPurge: boolean;
  onTrash: () => void;
  onPrivate: () => void;
  onShare: () => void;
  onRestore: () => void;
  onPurge: () => void;
}) {
  const styles = useStyles();
  const items = [
    canShare ? { id: "share", label: "공개로 전환", onClick: onShare } : null,
    canPrivate ? { id: "private", label: "비공개로 전환", onClick: onPrivate } : null,
    canTrash ? { id: "trash", label: "휴지통으로", onClick: onTrash } : null,
    canRestore ? { id: "restore", label: "복원", onClick: onRestore } : null,
    canPurge ? { id: "purge", label: "지우기", onClick: onPurge } : null,
  ].filter((item): item is { id: string; label: string; onClick: () => void } => !!item);
  if (!items.length) return <span className={styles.muted}>—</span>;
  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <FuiButton appearance="subtle" icon={<MoreHorizontal16Regular />} aria-label="문서 동작" />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {items.map((item) => (
            <MenuItem key={item.id} onClick={item.onClick}>
              {item.label}
            </MenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

function LinkMarks({ docId }: { docId: string }) {
  const styles = useStyles();
  const project = useProjectStore((s) => s.project);
  const schedule = useProjectStore((s) => s.derived.schedule);
  const links = project.documentLinks.filter((link) => link.documentId === docId);
  if (!links.length) return <span>—</span>;
  const catalog = {
    executionTasks: project.executionTasks,
    tasks: project.tasks.map((task) => ({ id: task.id, name: task.name, wbs: schedule.tasks.get(task.id)?.wbs, percentComplete: task.percentComplete })),
    records: project.records,
  };
  const shown = links.slice(0, DOCUMENT_LINK_PREVIEW);
  const rest = links.slice(DOCUMENT_LINK_PREVIEW);
  const restTitles = rest
    .map((link) => {
      const chip = presentLink(link, catalog);
      const label = [chip.code, chip.title].filter(Boolean).join(" ");
      return chip.missing ? `${label} · 삭제됨` : label;
    })
    .join("\n");
  return (
    <span className={styles.links}>
      {shown.map((link) => {
        const chip = presentLink(link, catalog);
        return <DocLinkMark key={link.id} chip={chip} onOpen={chip.missing ? undefined : () => openDocumentTarget(chip.targetKind, chip.targetId)} />;
      })}
      <DocLinkMore count={rest.length} titles={restTitles} />
    </span>
  );
}

